<template>
  <div>
    <transition name="fade">
      <div v-if="isOpen" class="overlay" @click="emit('close')"></div>
    </transition>

    <transition name="slide">
      <aside v-if="isOpen" class="setup-panel" role="dialog" aria-modal="true" :aria-label="$t('app.experience.detail.setupPanel.title')">
        <div class="panel-header">
          <h2>{{ $t('app.experience.detail.setupPanel.title') }}</h2>
          <button type="button" class="close-button" :aria-label="$t('app.experience.detail.setupPanel.close')" @click="emit('close')">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="setup-text">
            <p>{{ $t('app.experience.detail.setupPanel.intro') }}</p>
            <ul>
              <li>{{ $t('app.experience.detail.setupPanel.internet') }}</li>
              <li>{{ $t('app.experience.detail.setupPanel.microphone') }}</li>
              <li>{{ $t('app.experience.detail.setupPanel.speakers') }}</li>
            </ul>
            <p>{{ $t('app.experience.detail.setupPanel.recommendation') }}</p>
            <p>{{ $t('app.experience.detail.setupPanel.permission') }}</p>
            <p>{{ $t('app.experience.detail.setupPanel.network') }}</p>
          </div>

          <div class="mic-test">
            <p v-if="!micEnabled" class="mic-hint">{{ $t('app.experience.detail.micTest.unavailable') }}</p>

            <p v-else-if="state === 'requesting'" class="mic-hint">{{ $t('app.experience.detail.micTest.requesting') }}</p>

            <p v-else-if="state === 'unsupported'" class="mic-error">{{ $t('app.experience.detail.micTest.unsupported') }}</p>

            <template v-else-if="state === 'denied'">
              <p class="mic-error">{{ $t('app.experience.detail.micTest.denied') }}</p>
              <div>
                <Button class="primary icon icon-left" icon="bi-arrow-clockwise" :label="$t('app.experience.detail.micTest.retry')" @click="start()" />
              </div>
            </template>

            <div v-else-if="state === 'ready'" class="mic-field">
              <span>{{ $t('app.experience.detail.micTest.level') }}</span>
              <span class="mic-meter">
                <span class="mic-meter-value" :class="{ loud: level > 85 }" :style="{ width: `${level}%` }"></span>
              </span>
              <span class="mic-hint">{{ $t('app.experience.detail.micTest.levelHint') }}</span>
            </div>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  isOpen: boolean,
  // The microphone test is skipped during a live conversation or once the assessment is finished
  micEnabled: boolean,
}>();

const emit = defineEmits<{
  (e: 'close'): void,
}>();

type State = 'idle' | 'requesting' | 'denied' | 'unsupported' | 'ready';

const state = ref<State>('idle');
const level = ref(0);

let stream: MediaStream | null = null;
let audioContext: AudioContext | null = null;
let rafId: number | null = null;
// Incremented on every start/cleanup so a stale getUserMedia resolution is discarded
let session = 0;

function stopMeter() {
  if (rafId !== null) cancelAnimationFrame(rafId);
  rafId = null;
  audioContext?.close();
  audioContext = null;
  level.value = 0;
}

function stopStream() {
  stopMeter();
  stream?.getTracks().forEach((track) => track.stop());
  stream = null;
}

function startMeter(source: MediaStream) {
  audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  audioContext.createMediaStreamSource(source).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);

  const tick = () => {
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (const sample of samples) sum += sample * sample;
    const rms = Math.sqrt(sum / samples.length);
    const target = Math.min(1, rms * 4) * 100;
    // Fast attack, slow release so the bar stays readable
    level.value = target > level.value ? target : level.value * 0.85 + target * 0.15;
    rafId = requestAnimationFrame(tick);
  };
  tick();
}

async function start() {
  if (!navigator.mediaDevices?.getUserMedia) {
    state.value = 'unsupported';
    return;
  }
  const current = ++session;
  stopStream();
  state.value = 'requesting';
  try {
    const next = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (current !== session) {
      next.getTracks().forEach((track) => track.stop());
      return;
    }
    stream = next;
    startMeter(next);
    state.value = 'ready';
  } catch {
    if (current === session) state.value = 'denied';
  }
}

// Releases the microphone and every audio resource
function cleanup() {
  session++;
  stopStream();
  state.value = 'idle';
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close');
}

watch(() => props.isOpen && props.micEnabled, (active) => {
  if (active) start();
  else cleanup();
}, { immediate: true });

watch(() => props.isOpen, (open) => {
  if (open) document.addEventListener('keydown', onKeydown);
  else document.removeEventListener('keydown', onKeydown);
}, { immediate: true });

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  cleanup();
});
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
}

.setup-panel {
  position: fixed;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: var(--white-color);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  color: var(--secondary-color);
  z-index: 20;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--light-grey-color);

  h2 {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
  }

  .close-button {
    display: flex;
    padding: 4px;
    border: none;
    background: none;
    color: inherit;
    font-size: 17px;
    cursor: pointer;
  }
}

.panel-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 24px;
  padding: 24px;
  overflow-y: auto;
}

.setup-text {
  font-size: 14px;
  line-height: 1.5;

  p {
    margin: 0 0 12px;
  }

  ul {
    margin: 0 0 12px;
    padding-left: 20px;
  }
}

.mic-test {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--light-grey-color);
}

.mic-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;

  > span:first-child {
    font-weight: 700;
  }
}

.mic-meter {
  display: block;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background-color: var(--light-grey-color);

  .mic-meter-value {
    display: block;
    height: 100%;
    border-radius: 5px;
    background-color: $green-color;

    &.loud {
      background-color: $primary-color;
    }
  }
}

.mic-hint {
  margin: 0;
  font-size: 12px;
  color: var(--grey-50-color);
}

.mic-error {
  margin: 0;
  font-size: 13px;
  color: $red-color;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
